import clsx from "clsx";
import { Button, ButtonVariant, getButtonStyle } from "components/Form/Button";
import { Layout } from "components/Layout";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AppRoutes } from "types";
import { GalleryPreview } from "components/GalleryPreview";
import { UploadContext, UploadType } from "contexts/UploadContext";
import { usePrivacyModal } from "hooks/usePrivacyModal";
import {
  getAcceptedFileTypes,
  REQUIRED_IRIS_IMAGE_COUNT,
} from "lib/upload-files";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { useNavigate, useLocation } from "react-router-dom";

export function IrisUpload() {
  const [init, setInit] = useState<boolean>(false);
  const location = useLocation();

  const taskId = location.state?.taskId;
  const taskName = location.state?.taskName;

  const {
    files: images,
    setFiles: setImages,
    resetFiles: resetImages,
    upload,
  } = useContext(UploadContext);

  const { config } = useContext(CollectionDetailsContext);

  const privacyModal = usePrivacyModal();
  const navigate = useNavigate();

  const handleChangeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return;
      }

      if (event.target.files) {
        setImages(Array.from(event.target.files));
      }
    },
    [setImages]
  );

  useEffect(() => {
    if (!config || privacyModal.accepted === null) {
      return;
    }

    if (privacyModal.accepted === false) {
      navigate(AppRoutes.home);
      return;
    }

    if (!taskId) {
      navigate(AppRoutes.home);
      return;
    }

    setInit(true);
  }, [config, navigate, privacyModal.accepted, taskId]);

  if (!init) {
    return null;
  }

  return (
    <Layout
      className="space-y-12"
      toolbarClassName="z-[100] !py-3"
      toolbar={
        <>
          {images.length <= 0 && (
            <>
              <label className={clsx(getButtonStyle(), "cursor-pointer")}>
                <input
                  type="file"
                  accept={getAcceptedFileTypes("iris")}
                  multiple
                  onChange={handleChangeInput}
                  hidden
                />
                <span>Select iris images</span>
              </label>
              <button
                onClick={() => navigate(AppRoutes.home)}
                className="text-sm text-858494 underline hover:text-191c20 transition-colors"
              >
                Back to Home
              </button>
            </>
          )}

          {images.length > 0 && (
            <>
              <Button
                onClick={() => upload(UploadType.IRIS, taskId)}
                disabled={images.length !== REQUIRED_IRIS_IMAGE_COUNT}
              >
                Upload
              </Button>
              <Button onClick={resetImages} variant={ButtonVariant.FLAT}>
                Choose another
              </Button>
            </>
          )}
        </>
      }
    >
      <h1 className="text-32 font-bold text-gray-900 text-center">
        {images.length <= 0 && (taskName ? `${taskName}` : "Instructions")}
        {images.length > 0 && "Preview images"}
      </h1>

      {images.length > 0 && (
        <div className="text-858494 leading-5 text-center">
          <div
            className={clsx(
              "font-semibold mb-2",
              images.length === REQUIRED_IRIS_IMAGE_COUNT
                ? "text-green-600"
                : "text-red-600"
            )}
          >
            {images.length} of {REQUIRED_IRIS_IMAGE_COUNT} required images
            selected
          </div>
          Review iris images, and labels carefully. <br />
          Correct any mismatches before uploading.
        </div>
      )}

      {images.length <= 0 && (
        <div className="max-w-2xl mx-auto">
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <span className="flex-none w-8 h-8 bg-3461fd text-white rounded-full flex items-center justify-center text-sm font-medium">
                1
              </span>
              <p className="text-gray-700 pt-1">
                Download & open the Phone-Iris app from TestFlight.
              </p>
            </div>

            <div className="ml-12 flex justify-center">
              <img
                alt="Phone-Iris TestFlight"
                className="rounded-lg shadow-md max-w-sm"
                src={require("../assets/iris_capture/iris_testflight.JPG")}
                height={150}
                width={150}
              />
            </div>

            <div className="flex items-start gap-4">
              <span className="flex-none w-8 h-8 bg-3461fd text-white rounded-full flex items-center justify-center text-sm font-medium">
                2
              </span>
              <p className="text-gray-700 pt-1">
                Follow the in-app prompts to capture close-up images of your
                iris.
              </p>
            </div>

            <div className="flex flex-col gap-1 justify-center items-center">
              <img
                src={require("../assets/iris_capture/iris_instructions1.png")}
                alt="Iris capture instructions step 1"
                className="rounded-lg shadow-md max-w-sm"
                width={270}
              />
              <img
                src={require("../assets/iris_capture/iris_instructions2.png")}
                alt="Iris capture instructions step 2"
                className="rounded-lg shadow-md max-w-sm"
                height={150}
                width={270}
              />
            </div>

            <div className="flex items-start gap-4">
              <span className="flex-none w-8 h-8 bg-3461fd text-white rounded-full flex items-center justify-center text-sm font-medium">
                3
              </span>
              <p className="text-gray-700 pt-1">
                Locate your captures in the iPhone Camera Roll.
              </p>
            </div>
            <div className="flex items-start gap-4">
              <span className="flex-none w-8 h-8 bg-3461fd text-white rounded-full flex items-center justify-center text-sm font-medium">
                4
              </span>
              <p className="text-gray-700 pt-1">
                Return here and upload all saved iris images.
                <span className="block mt-1 font-semibold text-3461fd">
                  Note: You must select exactly {REQUIRED_IRIS_IMAGE_COUNT}{" "}
                  images to upload.
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {images.length > 0 && (
        <GalleryPreview
          isRemovable
          showLabels={
            config?.labels &&
            Object.keys(config.labels).some((key) => key !== "devices")
          }
        />
      )}
    </Layout>
  );
}
